import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

const ROW = 36;
const VISIBLE = 7;
const PAD_ROWS = 3;

export type IosTimeParts = {
  hour12: number;
  minute: number;
  meridiem: 'AM' | 'PM';
};

export function iosTimeFrom24(hour24: number, minute: number): IosTimeParts {
  const meridiem = hour24 >= 12 ? 'PM' : 'AM';
  let hour12 = hour24 % 12;
  if (hour12 === 0) hour12 = 12;
  return { hour12, minute: Math.min(59, Math.max(0, minute)), meridiem };
}

export function iosTimeTo24(parts: IosTimeParts): { h: number; m: number } {
  let h = parts.hour12 % 12;
  if (parts.meridiem === 'PM') h += 12;
  return { h, m: parts.minute };
}

const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
const MERIDIEMS = ['AM', 'PM'];

type ColumnProps = {
  options: readonly string[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  loop: boolean;
  ariaLabel: string;
  width: number;
};

const WheelColumn: React.FC<ColumnProps> = ({
  options,
  selectedIndex,
  onSelect,
  loop,
  ariaLabel,
  width,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const timer = useRef<number | null>(null);
  const onSelectRef = useRef(onSelect);
  const selectedRef = useRef(selectedIndex);
  onSelectRef.current = onSelect;
  selectedRef.current = selectedIndex;

  const count = options.length;
  const scrollIndex = loop ? count + selectedIndex : selectedIndex;
  const [visualCenter, setVisualCenter] = useState(scrollIndex);
  const copies = loop ? [0, 1, 2] : [0];
  const drag = useRef<{ y: number; top: number } | null>(null);
  const dragged = useRef(false);

  const settleRef = useRef<() => void>(() => {});
  settleRef.current = () => {
    const el = ref.current;
    if (!el) return;
    const idx = Math.round(el.scrollTop / ROW);
    const logical = loop
      ? ((idx % count) + count) % count
      : Math.max(0, Math.min(count - 1, idx));
    setVisualCenter(loop ? count + logical : logical);
    if (logical !== selectedRef.current) onSelectRef.current(logical);
  };

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || drag.current) return;
    const top = scrollIndex * ROW;
    setVisualCenter(scrollIndex);
    if (Math.abs(el.scrollTop - top) > 1) el.scrollTop = top;
  }, [scrollIndex]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      setVisualCenter(Math.round(el.scrollTop / ROW));
      if (timer.current != null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => settleRef.current(), 80);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (timer.current != null) window.clearTimeout(timer.current);
    };
  }, []);

  return (
    <Box
      ref={ref}
      role="spinbutton"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-valuenow={selectedIndex}
      aria-valuemin={0}
      aria-valuemax={count - 1}
      aria-valuetext={options[selectedIndex] ?? ''}
      onKeyDown={e => {
        if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
        e.preventDefault();
        const dir = e.key === 'ArrowDown' ? 1 : -1;
        const next = loop
          ? (selectedIndex + dir + count) % count
          : Math.max(0, Math.min(count - 1, selectedIndex + dir));
        onSelect(next);
      }}
      onPointerDown={e => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        const el = ref.current;
        if (!el) return;
        dragged.current = false;
        drag.current = { y: e.clientY, top: el.scrollTop };
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={e => {
        if (!drag.current || !ref.current) return;
        const dy = e.clientY - drag.current.y;
        if (Math.abs(dy) > 3) dragged.current = true;
        ref.current.scrollTop = drag.current.top - dy;
      }}
      onPointerUp={() => {
        const wasDrag = dragged.current;
        drag.current = null;
        if (wasDrag) window.setTimeout(() => settleRef.current(), 150);
      }}
      onPointerCancel={() => {
        drag.current = null;
      }}
      sx={{
        height: '100%',
        width,
        overflowY: 'auto',
        scrollSnapType: 'y mandatory',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        pt: `${PAD_ROWS * ROW}px`,
        pb: `${PAD_ROWS * ROW}px`,
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        outline: 'none',
        '&:focus-visible': { outline: '2px solid #2D4B73', outlineOffset: -2 },
      }}
    >
      {copies.map(copy =>
        options.map((label, i) => {
          const absolute = copy * count + i;
          const dist = Math.abs(absolute - visualCenter);
          const selected = dist === 0;
          return (
            <Box
              key={`${copy}-${label}`}
              onClick={() => {
                if (dragged.current) {
                  dragged.current = false;
                  return;
                }
                onSelect(i);
              }}
              sx={{
                height: ROW,
                scrollSnapAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                fontVariantNumeric: 'tabular-nums',
                fontWeight: selected ? 600 : 400,
                fontSize: selected ? 22 : 18,
                lineHeight: 1,
                color: selected ? '#1c1c1e' : dist === 1 ? '#8e8e93' : '#c7c7cc',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              {label}
            </Box>
          );
        })
      )}
    </Box>
  );
};

type IosTimeWheelProps = {
  value: IosTimeParts;
  onChange: (value: IosTimeParts) => void;
};

const IosTimeWheel: React.FC<IosTimeWheelProps> = ({ value, onChange }) => {
  const hourIndex = Math.min(11, Math.max(0, value.hour12 - 1));
  const minuteIndex = Math.min(59, Math.max(0, value.minute));
  const meridiemIndex = value.meridiem === 'PM' ? 1 : 0;

  return (
    <Box
      role="group"
      aria-label="Time"
      sx={{
        position: 'relative',
        height: ROW * VISIBLE,
        width: 280,
        bgcolor: '#e5e5ea',
        userSelect: 'none',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          height: '100%',
          maskImage: 'linear-gradient(to bottom, transparent, #000 22%, #000 78%, transparent)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent, #000 22%, #000 78%, transparent)',
        }}
      >
        <WheelColumn
          options={HOURS}
          selectedIndex={hourIndex}
          loop
          ariaLabel="Hour"
          width={76}
          onSelect={index => onChange({ ...value, hour12: index + 1 })}
        />
        <WheelColumn
          options={MINUTES}
          selectedIndex={minuteIndex}
          loop
          ariaLabel="Minute"
          width={76}
          onSelect={index => onChange({ ...value, minute: index })}
        />
        <WheelColumn
          options={MERIDIEMS}
          selectedIndex={meridiemIndex}
          loop={false}
          ariaLabel="AM or PM"
          width={76}
          onSelect={index => onChange({ ...value, meridiem: index === 1 ? 'PM' : 'AM' })}
        />
      </Box>
      <Box
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          zIndex: 1,
          top: PAD_ROWS * ROW,
          height: ROW,
          left: 10,
          right: 10,
          borderTop: '1px solid rgba(60, 60, 67, 0.36)',
          borderBottom: '1px solid rgba(60, 60, 67, 0.36)',
        }}
      />
    </Box>
  );
};

export default IosTimeWheel;
